import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, Flag, ListChecks, ArrowLeft, Settings as SettingsIcon } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import {
  ASSET_REQUEST_TYPES, ASSET_REQUEST_PRIORITIES, DEFAULT_ASSET_REQUEST_TYPE,
  DEFAULT_ASSET_REQUEST_PRIORITY, assetReqTypeClass,
} from "../../constants/assetRequest";

/* Asset-request workflow settings, presented as its own settings layout: a left
   secondary side-menu of sections + a right content pane. Reached via the gear
   on the Asset Requests header (not the main sidebar). Request types are a fixed
   frontend list (constants/assetRequest.js) — shown read-only. */

const SECTIONS = [
  { id: "types", label: "Issue Types", icon: Tag },
  { id: "priorities", label: "Priorities", icon: Flag },
  { id: "rules", label: "Request Rules", icon: ListChecks },
];

const RULES = [
  ["Require admin approval before issuing", true],
  ["Allow employees to request multiple assets", true],
  ["New employee requests default to Incident Request", true],
  ["Auto-flag high-value requests for finance review", false],
];

const Pill = ({ on, children }) => (
  <span className={cn(
    "rounded-full px-2.5 py-0.5 text-xs font-semibold",
    on ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
       : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
  )}>{children}</span>
);

const SectionHeader = ({ title, desc }) => (
  <div className="mb-4">
    <h3 className="text-base font-semibold">{title}</h3>
    {desc && <p className="mt-0.5 text-sm text-muted-foreground">{desc}</p>}
  </div>
);

const AssetRequestSettings = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState("types");

  return (
    <ContainerCard>
      {/* compact page header */}
      <div className="flex items-center gap-3 p-3">
        <button
          onClick={() => navigate("/assetmanagement/assetrequest")}
          className="grid h-9 w-9 place-items-center rounded-lg bg-sidebar-primary/10 text-sidebar-primary"
          title="Back to Asset Requests"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2">
          <SettingsIcon className="h-5 w-5 text-sidebar-primary" />
          <div>
            <h1 className="text-lg font-semibold leading-tight">Asset Request Settings</h1>
            <p className="text-xs text-muted-foreground">Request types, priorities and workflow rules</p>
          </div>
        </div>
      </div>

      {/* settings layout: side-menu + content */}
      <div className="grid grid-cols-1 gap-4 p-3 md:grid-cols-[220px_1fr]">
        {/* secondary side-menu */}
        <nav className="flex flex-row gap-1 overflow-x-auto rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-2 md:flex-col md:overflow-visible">
          {SECTIONS.map((s) => {
            const on = active === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                className={cn(
                  "flex items-center gap-2.5 whitespace-nowrap rounded-xl px-3 py-2.5 text-sm font-medium transition-colors md:w-full",
                  on
                    ? "bg-sidebar-primary/10 text-sidebar-primary"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                )}
              >
                <s.icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-left">{s.label}</span>
              </button>
            );
          })}
        </nav>

        {/* content pane */}
        <div className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          {active === "types" && (
            <>
              <SectionHeader
                title="Issue Types"
                desc="What an employee is requesting. The marked type is preselected on the form; admins can reclassify any request from its detail screen."
              />
              <div className="flex flex-col gap-2">
                {ASSET_REQUEST_TYPES.map((t) => (
                  <div key={t} className="flex items-center gap-3 rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-4 py-2.5">
                    <span className={cn("h-2.5 w-2.5 rounded-full", assetReqTypeClass(t).split(" ")[0])} />
                    <span className="flex-1 text-sm font-semibold">{t}</span>
                    {t === DEFAULT_ASSET_REQUEST_TYPE && <Pill on>User default</Pill>}
                  </div>
                ))}
              </div>
            </>
          )}

          {active === "priorities" && (
            <>
              <SectionHeader title="Priorities" desc="Selectable priority levels when raising a request." />
              <div className="flex flex-wrap gap-2">
                {ASSET_REQUEST_PRIORITIES.map((p) => (
                  <span key={p} className="flex items-center gap-2 rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-1.5 text-sm font-medium">
                    {p}
                    {p === DEFAULT_ASSET_REQUEST_PRIORITY && <Pill on>Default</Pill>}
                  </span>
                ))}
              </div>
            </>
          )}

          {active === "rules" && (
            <>
              <SectionHeader title="Request Rules" desc="Workflow defaults (display only in this slice)." />
              {RULES.map(([label, on]) => (
                <div key={label} className="flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 py-3 last:border-0">
                  <span className="flex-1 text-sm font-medium">{label}</span>
                  <Pill on={on}>{on ? "On" : "Off"}</Pill>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </ContainerCard>
  );
};

export default AssetRequestSettings;
